import {
  IConversationStatistics,
  listConversationStatistics,
} from '@brands/services/communication/listConversationStatistics';
import { statisticsRefreshHandled } from '@brands/store/slices/conversationStatisticsSlice';
import { RootState } from '@brands/store/store';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const useConversationStatistics = (
  filter: 'active' | 'archived' = 'active',
  userRole?: string,
): {
  statistics: IConversationStatistics[];
  refresh: () => Promise<void>;
  loading: boolean;
  error: Error | null;
} => {
  const [statistics, setStatistics] = useState<IConversationStatistics[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const dispatch = useDispatch();
  const triggerRefresh = useSelector((state: RootState) => state.conversationStatistics.triggerRefresh);

  const fetchStatistics = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await listConversationStatistics(filter, userRole);
      setStatistics(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filter, userRole]);

  // Initial fetch & refetch on filter change
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Listen for redux-based refresh trigger — fetch silently to avoid flickering the UI
  useEffect(() => {
    if (triggerRefresh) {
      fetchStatistics(false).then(() => {
        dispatch(statisticsRefreshHandled());
      });
    }
  }, [triggerRefresh, dispatch, fetchStatistics]);

  return {
    statistics,
    refresh: fetchStatistics,
    loading,
    error,
  };
};
